import { useNetworkDetails } from "@features/ax/assetRegistry/networks/hooks";
import { logEvent } from "@shared/analytics/analytics";
import { CustomTooltip } from "@shared/components/CustomTooltip";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tabs } from "@shared/ui/tabs";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { Eye } from "lucide-react";
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { NetworkFinanceOverview } from "./NetworkFinanceOverview";

interface NetworkFinanceTabProps {
  networkData?: any;
}

export const NetworkFinanceTab: React.FC<NetworkFinanceTabProps> = () => {
  const { id } = useParams<{ id: string }>();
  const { data: financeData } = useNetworkDetails({
    tab: "finance/overview",
    networkId: id,
    remote_utility_id: getRemoteUtilityId(),
  });
  const currency = useCurrency();
  useEffect(() => {
    logEvent("finance_tab_viewed");
  }, []);
  // Extract data from hook response
  const networkFinancialData = {
    currentBookValue:
      financeData?.result?.networkBookValue?.currentBookValue || 0,
    initialCapitalCost:
      financeData?.result?.networkBookValue?.initialCapitalCost || 0,
    accumulatedDepreciation:
      financeData?.result?.networkBookValue?.accumulatedDepreciation || 0,
    salvageValue: financeData?.result?.networkBookValue?.salvageValue || 0,
  };

  const stats = [
    {
      value: `${currency} ${networkFinancialData.currentBookValue}`,
      label: "Current Book Value",
      valueClassName: "text-3xl font-bold text-blue-600",
      tooltip:
        "The current value of the network on the balance sheet after accounting for all depreciation to date.",
    },
    {
      value: `${currency} ${networkFinancialData.initialCapitalCost}`,
      label: "Initial Capital Cost",
      valueClassName: "text-lg font-semibold",
      tooltip: "The total original cost to acquire and install this network.",
    },
    {
      value: `${currency} ${networkFinancialData.accumulatedDepreciation}`,
      label: "Accumulated Depreciation",
      valueClassName: "text-lg font-semibold text-red-600",
      tooltip:
        "The sum of all depreciation expenses recorded for this network over its life.",
    },
    {
      value: `${currency} ${networkFinancialData.salvageValue}`,
      label: "Salvage Value",
      valueClassName: "text-lg font-semibold",
      tooltip:
        "The estimated resale value of the network at the end of its useful life.",
    },
  ];

  const tabComponents = {
    overview: {
      label: "Overview",
      icon: <Eye className="h-4 w-4" />,
      component: <NetworkFinanceOverview />,
    },
    // history: {
    //   label: "History",
    //   icon: <History className="h-4 w-4" />,
    //   component: <HistoryTab />,
    // },
    // Removed for now will need after
  };

  const urlMapping = {
    overview: "overview",
    history: "history",
  };

  return (
    <div className="space-y-6">
      {/* Main Financial Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="page-title">Network Book Value</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {stats.map((stat, idx) => (
              <div key={idx}>
                <div className={stat.valueClassName}>{stat.value}</div>
                <div className="text-sm text-gray-600 flex gap-2">
                  {stat.label}
                  <CustomTooltip message={stat.tooltip} />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Tabs
        defaultValue="overview"
        tabComponents={tabComponents}
        urlMapping={urlMapping}
        tabsListClassName=" mb-6"
        // tabsListClassName="grid w-full grid-cols-2 mb-6"
        idPrefix="finance-tab"
        level={1}
      />
    </div>
  );
};
